import { useState, useEffect } from 'react';
import api from '../../../services/api';
import type { Ad } from '../../../types';
import './AdDetailModal.css';

interface AdDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  adId: string | null;
  allAdIds?: string[];
  onAdChange?: (adId: string) => void;
  onEdit?: (adId: string) => void;
  useAdminApi?: boolean; // If true, use /api/admin/content/ads/:id (for pending ads)
  hideViewFullDetails?: boolean; // If true, hide "查看完整詳情" button
  showApprovalActions?: boolean; // If true, show approve/reject buttons for admin
  onApprove?: (adId: string) => void;
  onReject?: (adId: string) => void;
}

const AdDetailModal = ({
  isOpen,
  onClose,
  adId,
  allAdIds = [],
  onAdChange,
  onEdit,
  useAdminApi = false,
  hideViewFullDetails = false,
  showApprovalActions = false,
  onApprove,
  onReject
}: AdDetailModalProps) => {
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Calculate current index and prev/next availability
  const currentIndex = allAdIds.indexOf(adId || '');
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allAdIds.length - 1;
  const prevAdId = hasPrev ? allAdIds[currentIndex - 1] : null;
  const nextAdId = hasNext ? allAdIds[currentIndex + 1] : null;

  useEffect(() => {
    if (!isOpen || !adId) {
      setAd(null);
      setError(null);
      return;
    }

    const loadAd = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use admin API if useAdminApi is true (for pending ads), otherwise use public API
        const endpoint = useAdminApi ? `/api/admin/content/ads/${adId}` : `/api/ads/${adId}`;
        const response = await api.get<Ad>(endpoint);
        if (response.success && response.data) {
          setAd(response.data);
        } else {
          setError('找不到此刊登');
        }
      } catch (err) {
        console.error('Error loading ad:', err);
        setError('載入刊登時發生錯誤');
      } finally {
        setLoading(false);
      }
    };

    loadAd();
  }, [isOpen, adId, useAdminApi]);

  const handleCall = () => {
    if (ad?.phone) {
      window.location.href = `tel:${ad.phone}`;
    }
  };

  const handleViewFullDetails = () => {
    // Navigate to full detail page
    window.location.href = `/ads/${adId}`;
  };

  const handlePrevious = () => {
    if (prevAdId && onAdChange) {
      setActiveImageIndex(0);
      setError(null);
      onAdChange(prevAdId);
    }
  };

  const handleNext = () => {
    if (nextAdId && onAdChange) {
      setActiveImageIndex(0);
      setError(null);
      onAdChange(nextAdId);
    }
  };

  if (!isOpen) return null;

  const images = ad?.imageUrls && ad.imageUrls.length > 0 ? ad.imageUrls : [];

  return (
    <div className="ad-detail-modal-overlay" onClick={onClose}>
      <div className="ad-detail-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Navigation Bar */}
        <div className="ad-detail-modal__nav">
          <button
            className="ad-detail-modal__nav-btn"
            onClick={handlePrevious}
            disabled={!hasPrev}
            aria-label="上一則"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            <span>上一則</span>
          </button>
          <span className="ad-detail-modal__nav-counter">
            {currentIndex + 1} / {allAdIds.length || 1}
          </span>
          <button
            className="ad-detail-modal__nav-btn"
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="下一則"
          >
            <span>下一則</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="ad-detail-modal__loading">載入中...</div>
        ) : error || !ad ? (
          <div className="ad-detail-modal__error">
            <p>{error || '找不到此刊登'}</p>
          </div>
        ) : (
          <div className="ad-detail-modal__content">
            {/* Images */}
            {images.length > 0 && (
              <div className="ad-detail-modal__media">
                <div className="ad-detail-modal__main-image">
                  <img src={images[activeImageIndex]} alt={ad.title} />
                </div>
                {images.length > 1 && (
                  <div className="ad-detail-modal__thumbnails">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        className={`ad-detail-modal__thumbnail ${
                          index === activeImageIndex ? 'active' : ''
                        }`}
                        onClick={() => setActiveImageIndex(index)}
                      >
                        <img src={img} alt={`${ad.title} - 圖片 ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info */}
            <div className="ad-detail-modal__info">
              <h1 className="ad-detail-modal__title">{ad.title}</h1>

              <div className="ad-detail-modal__company">
                <strong>{ad.storeName}</strong>
              </div>

              <div className="ad-detail-modal__tags">
                <span className="tag tag--region">📍 {ad.region}</span>
                {ad.district && (
                  <span className="tag tag--region">📍 {ad.district}</span>
                )}
                <span className="tag tag--service">🔧 {ad.category}</span>
                {ad.tags?.map((tag) => (
                  <span key={tag} className="tag tag--service">🔧 {tag}</span>
                ))}
              </div>

              {ad.description && (
                <div className="ad-detail-modal__section">
                  <h3>服務描述</h3>
                  <p>{ad.description}</p>
                </div>
              )}

              {ad.address && (
                <div className="ad-detail-modal__section">
                  <h3>地址</h3>
                  <p>{ad.address}</p>
                </div>
              )}

              {ad.priceRange && (
                <div className="ad-detail-modal__section">
                  <h3>價格範圍</h3>
                  <p>{ad.priceRange}</p>
                </div>
              )}

              <div className="ad-detail-modal__section">
                <h3>聯絡資訊</h3>
                <div className="ad-detail-modal__contact">
                  {ad.phone && <p><strong>電話：</strong> {ad.phone}</p>}
                  {ad.address && <p><strong>地址：</strong> {ad.address}</p>}
                </div>
              </div>

              <div className="ad-detail-modal__actions">
                {showApprovalActions && adId ? (
                  <>
                    <button className="btn btn--danger" onClick={() => onReject?.(adId)}>
                      ❌ 拒絕
                    </button>
                    <button className="btn btn--success" onClick={() => onApprove?.(adId)}>
                      ✅ 通過
                    </button>
                  </>
                ) : (
                  <>
                    {ad.phone && (
                      <button className="btn btn--primary" onClick={handleCall}>
                        📞 立即撥打
                      </button>
                    )}
                    {onEdit && adId && (
                      <button className="btn btn--secondary" onClick={() => onEdit(adId)}>
                        ✏️ 編輯
                      </button>
                    )}
                  </>
                )}
                {!hideViewFullDetails && (
                  <button className="btn btn--outline" onClick={handleViewFullDetails}>
                    查看完整詳情 →
                  </button>
                )}
              </div>

              <div className="ad-detail-modal__stats">
                <span>👁️ 瀏覽 {ad.viewCount} 次</span>
                <span>🖱️ 點擊 {ad.clickCount} 次</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdDetailModal;
